import React, { useEffect,useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppointmentTypes } from '@features/appointment-types';
import { DurationSchedulePicker  } from '@features/appointments';
import { Box, Button } from '@mui/material';
import { add, differenceInMinutes,format } from 'date-fns';

import type { EncounterFormData } from '../types';

interface EncounterScheduleDurationProps {
  encounterId?: string; // Optional during migration
  onScheduleSelect: (date: Date | undefined, duration: number | undefined) => void;
  onClose?: () => void;
  autoSelectMode?: boolean;
  initialDateTime?: Date;
  initialDuration?: number;
}

const FALLBACK_DURATION = 30;

const EncounterScheduleDuration: React.FC<EncounterScheduleDurationProps> = ({
  encounterId,
  onScheduleSelect,
  onClose,
  autoSelectMode = false,
  initialDateTime,
  initialDuration,
}) => {
  // Form context (replaces RTK Query)
  const { watch } = useFormContext<EncounterFormData>();
  const appointment = watch('appointment');
  const template_id = watch('template_id');
  const start_time = watch('start_time');
  const end_time = watch('end_time');

  const { appointmentTypes, isLoading: isLoadingAppointmentTypes } = useAppointmentTypes();
  // Round to nearest 15 minute increment
  const now = new Date();
  const minutes = now.getMinutes();
  const remainder = minutes % 15;
  if (remainder !== 0) {
    // Round up or down to nearest 15 min
    const roundTo = remainder < 8 ? -remainder : 15 - remainder;
    now.setMinutes(minutes + roundTo);
    now.setSeconds(0);
    now.setMilliseconds(0);
  }

  // Initialize with existing values if available, or with initialDateTime if provided
  const defaultDate =
    initialDateTime || (appointment ? new Date(appointment.start_time) : now);

  const [date, setDate] = useState(format(defaultDate, 'yyyy-MM-dd'));
  const [time, setTime] = useState(format(defaultDate, 'HH:mm'));

  // Initialize duration from props, state, or existing start/end times
  const [selectedDuration, setSelectedDuration] = useState(
    initialDuration ||
      appointment?.duration ||
      (start_time && end_time
        ? differenceInMinutes(new Date(end_time), new Date(start_time))
        : null),
  );

  // Slim durations that match our new UI
  const SLIM_DURATIONS = [
    { label: '30m', minutes: 30 },
    { label: '45m', minutes: 45 },
    { label: '1h', minutes: 60 },
  ];

  // Check if the current duration (including fallback) matches any of our slim preset durations
  const currentDuration =
    initialDuration ??
    appointment?.duration ??
    (start_time && end_time
      ? differenceInMinutes(new Date(end_time), new Date(start_time))
      : null);

  const isInitialDurationPreset = SLIM_DURATIONS.some(preset => {
    return preset.minutes === currentDuration;
  });

  // For existing encounters with a start and end time
  const [startTime, setStartTime] = useState<string>(
    appointment?.start_time ? format(new Date(appointment.start_time), 'HH:mm') : time,
  );
  const [endTime, setEndTime] = useState<string>(() => {
    if (appointment?.start_time && appointment?.duration) {
      const startDateTime = new Date(appointment.start_time);
      const endDateTime = add(startDateTime, {
        minutes: appointment.duration,
      });
      return format(endDateTime, 'HH:mm');
    } else {
      return '';
    }
  });

  // Only set custom duration if we have a duration that doesn't match our slim presets
  const [isCustomDuration, setIsCustomDuration] = useState(!isInitialDurationPreset);

  // NOTE: Removed auto-apply defaults useEffect - now handled by EncounterFormProvider
  // with deferred defaults that only save on first user interaction

  useEffect(() => {
    // Update date and time when initialDateTime changes
    if (initialDateTime) {
      setDate(format(initialDateTime, 'yyyy-MM-dd'));
      setTime(format(initialDateTime, 'HH:mm'));
    }
    // Handle other state updates as before
    else if (appointment) {
      setDate(format(new Date(appointment.start_time), 'yyyy-MM-dd'));
      setTime(format(new Date(appointment.start_time), 'HH:mm'));
    }

    // Update duration when initialDuration changes
    if (initialDuration !== undefined) {
      setSelectedDuration(initialDuration);

      // Check if duration matches our slim preset durations
      const isPresetDuration = SLIM_DURATIONS.some(preset => preset.minutes === initialDuration);

      setIsCustomDuration(!isPresetDuration);

      // Calculate and set start/end times
      if (initialDateTime) {
        const startDateTime = initialDateTime;
        const endDateTime = add(startDateTime, { minutes: initialDuration });

        setStartTime(format(startDateTime, 'HH:mm'));
        setEndTime(format(endDateTime, 'HH:mm'));
      }
    }
    // Handle duration from existing state if no initialDuration
    else if (appointment) {
      setSelectedDuration(appointment.duration);
    } else if (start_time && end_time) {
      setStartTime(format(new Date(start_time), 'HH:mm'));
      setEndTime(format(new Date(end_time), 'HH:mm'));
      setSelectedDuration(
        differenceInMinutes(new Date(end_time), new Date(start_time)),
      );
      setIsCustomDuration(true);
    }
  }, [initialDateTime, initialDuration, appointment, start_time, end_time]);

  // Add effect for autoSelectMode to automatically call onScheduleSelect when values change
  useEffect(() => {
    if (autoSelectMode && date && time) {
      autoSelect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, time, selectedDuration, isCustomDuration, startTime, endTime]);

  // Function to calculate values and call onScheduleSelect
  const autoSelect = () => {
    if (date && time) {
      const startDateTime = new Date(`${date}T${time}`);
      let durationMinutes: number | undefined;

      if (isCustomDuration && startTime && endTime) {
        // Custom duration using specific start/end times
        const startDate = new Date(`${date}T${startTime}`);
        const endDate = new Date(`${date}T${endTime}`);

        if (startDate < endDate) {
          durationMinutes = differenceInMinutes(endDate, startDate);
          onScheduleSelect(startDateTime, durationMinutes);
        }
      } else if (!isCustomDuration && selectedDuration) {
        // Preset duration
        durationMinutes = selectedDuration;
        onScheduleSelect(startDateTime, durationMinutes);
      } else {
        // Only schedule, no duration
        onScheduleSelect(startDateTime, undefined);
      }
    }
  };

  const handleDurationSelect = (minutes: number) => {
    setSelectedDuration(minutes);
    setIsCustomDuration(false);

    // If we have a date and time, auto-calculate the end time
    if (date && time) {
      const startDateTime = new Date(`${date}T${time}`);
      const endDateTime = add(startDateTime, { minutes });

      setStartTime(format(startDateTime, 'HH:mm'));
      setEndTime(format(endDateTime, 'HH:mm'));
    }
  };

  const handleDone = () => {
    let startDateTime: Date | undefined;
    let durationMinutes: number = 0; // Default to 0 if no duration selected

    if (date && time) {
      startDateTime = new Date(`${date}T${time}`);

      if (isCustomDuration && startTime && endTime) {
        // Custom duration using specific start/end times
        const startDate = new Date(`${date}T${startTime}`);
        const endDate = new Date(`${date}T${endTime}`);

        if (startDate < endDate) {
          durationMinutes = differenceInMinutes(endDate, startDate);
        }
      } else if (!isCustomDuration && selectedDuration) {
        // Preset duration
        durationMinutes = selectedDuration;
      }

      // Call onScheduleSelect with startDateTime and duration always defined
      onScheduleSelect(startDateTime, durationMinutes);
    }

    onClose?.();
  };

  const handleCustomDuration = () => {
    setIsCustomDuration(true);

    // If preset was selected, initialize custom times
    if (selectedDuration && date && time) {
      const startDateTime = new Date(`${date}T${time}`);
      const endDateTime = add(startDateTime, { minutes: selectedDuration });

      setStartTime(format(startDateTime, 'HH:mm'));
      setEndTime(format(endDateTime, 'HH:mm'));
    } else if (date && time) {
      // Otherwise just use the scheduled time as start time
      setStartTime(time);
      setEndTime(time);
    }
  };

  const canSave =
    date &&
    time &&
    (!isCustomDuration ||
      (isCustomDuration &&
        startTime &&
        endTime &&
        new Date(`${date}T${startTime}`) <= new Date(`${date}T${endTime}`)));

  // Handle date, time, and duration changes
  const handleDateChange = (newDate: string) => {
    setDate(newDate);
  };

  const handleStartTimeChange = (newTime: string) => {
    setStartTime(newTime);
    setTime(newTime); // Update the main time as well when in custom mode
  };

  const handleEndTimeChange = (newTime: string) => {
    setEndTime(newTime);
  };

  return (
    <Box sx={{ pt: 1 }}>
      <DurationSchedulePicker
        date={date}
        startTime={startTime}
        endTime={endTime}
        selectedDuration={selectedDuration || FALLBACK_DURATION}
        isCustomDuration={isCustomDuration}
        onDateChange={handleDateChange}
        onStartTimeChange={handleStartTimeChange}
        onEndTimeChange={handleEndTimeChange}
        onDurationSelect={handleDurationSelect}
        onCustomDurationSelect={handleCustomDuration}
        title="Schedule"
        description=""
        slimDurations={SLIM_DURATIONS}
      />

      {/* Action buttons - Only shown if not in autoSelectMode */}
      {!autoSelectMode && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" onClick={handleDone} disabled={!canSave}>
            Done
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EncounterScheduleDuration;
